import {Frequency} from "@indebted/payment-arrangement/Slider/Frequency"

import {get, post} from "./http"
import {getPaymentFormPage} from "./paymentArrangement/paymentFormPageAPI"
import {initiateSetup} from "./paymentArrangement/initiateSetup"
import {cancel} from "./paymentArrangement/cancel"
import {postSetup} from "./paymentArrangement/postSetup"
import {pollConfirmation} from "./paymentArrangement/pollConfirmation"

type PaymentArrangementSetupConfigResponse = {
	ClientName: string
	Currency: string
	OutstandingBalance: number
	RecommendedFrequency: Frequency
	Recommendations: Record<Frequency, {NumberOfPayments: number; MaxDuration: number}>
}

function PaymentArrangementAPI() {
	function waitForOneSecond() {
		return new Promise((resolve) => setTimeout(resolve, 1000))
	}

	async function getManagePage({paymentArrangementID}: {paymentArrangementID: string}) {
		return await get(`/app/payment-arrangements/${paymentArrangementID}/manage`)
	}

	const getStatusPage = async (paymentArrangementID: string, status: string) => {
		return await get(`/app/payment-arrangements/${paymentArrangementID}/status/${status}`)
	}

	const initiateDebit = async (paymentArrangementID: string, debitID: string) => {
		return await post(`/app/payment-arrangements/${paymentArrangementID}/initiate-debit`, {DebitID: debitID})
	}

	const pollInstalmentConfirmation = async (
		paymentArrangementID: string,
		signal: AbortSignal,
		numberOfRetries: number = 0,
	): Promise<unknown> => {
		if (signal.aborted) throw new Error("Polling aborted")

		const result = await get(`/app/payment-arrangements/${paymentArrangementID}/instalment-status`)
		if (result.Status !== "initiated" && result.Status != "submitted") {
			return result
		}

		if (numberOfRetries === 59) {
			return result
		}

		await waitForOneSecond()
		return await pollInstalmentConfirmation(paymentArrangementID, signal, numberOfRetries + 1)
	}

	async function getSetupConfig(paymentArrangementID: string): Promise<PaymentArrangementSetupConfigResponse> {
		return await get(`/app/payment-arrangements/${paymentArrangementID}/configuration`)
	}

	return {
		initiateSetup,
		getPaymentFormPage,
		pollConfirmation,
		getManagePage,
		getStatusPage,
		cancel,
		initiateDebit,
		pollInstalmentConfirmation,
		getSetupConfig,
		postSetup,
	}
}

export const paymentArrangementAPI = PaymentArrangementAPI()

export type {PaymentFormPageResponse} from "./paymentArrangement/paymentFormPageAPI"
export type {InitiateSetupResponse} from "./paymentArrangement/initiateSetup"
export type {PostSetupResponse} from "./paymentArrangement/postSetup"
export type {PollConfirmationResponse} from "./paymentArrangement/pollConfirmation"
